import { AAASMessages } from '@va/aaas/shared/constants';
import { getSeeAllPlansMessage, getUniquePackagesByName, selectAllowUpgrade } from '@va/aaas/shared/selectors';
import { AaasPackage, RootState } from '@va/aaas/shared/types';
import { is3ASUnlimitedFreeTrialPlan, PlanNames } from '@va/constants';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { useLocalizationContext } from '@va/localization';
import { SubscriptionTypeEnum } from '@va/standalone/shared/constants';
import {
  Button,
  fontWeights,
  Heading3,
  Paragraph,
  paragraphSizes,
  TabbedButton,
  TabbedButtonProps,
} from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { addNumberSeparator } from '@va/util/helpers';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { PricingPlanTile } from './PricingPlanTile';
import { getAaasRelevantPlans } from './utils';

type AaasPricingPlanSuggestionModalProps = {
  getRecommendedPlan: (plans: AaasPackage[]) => AaasPackage | null | undefined;
  hint: string;
  title: string;
  subtitle: ReactNode;
  onClose: () => void;
  isLoading: boolean;
};

export const AaasPricingPlanSuggestionModal = ({
  getRecommendedPlan,
  hint,
  subtitle,
  title,
  onClose,
  isLoading = false,
}: AaasPricingPlanSuggestionModalProps) => {
  const { translate, locale } = useLocalizationContext();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionTypeEnum>(SubscriptionTypeEnum.YEARLY);
  const monthlyPackageOptions = useSelector((state: RootState) => getUniquePackagesByName(state, 'monthly'));
  const yearlyPackageOptions = useSelector((state: RootState) => getUniquePackagesByName(state, 'yearly'));
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const seeAllPlansMessage = useSelector(getSeeAllPlansMessage);

  const plans = useMemo(() => {
    const planOptions = subscriptionType === SubscriptionTypeEnum.YEARLY ? yearlyPackageOptions : monthlyPackageOptions;
    return planOptions.filter((p) => p.name !== PlanNames.Free && !is3ASUnlimitedFreeTrialPlan(p.name));
  }, [subscriptionType, yearlyPackageOptions, monthlyPackageOptions]);

  const recommendedPlan = useMemo(() => getRecommendedPlan(plans), [getRecommendedPlan, plans]);
  const otherPlans = useMemo(() => getAaasRelevantPlans(recommendedPlan, plans), [recommendedPlan, plans]);

  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(recommendedPlan?.id);

  const closeModal = useCallback(() => {
    onClose?.();
    setIsOpen(false);
  }, [onClose]);

  const handleUpgrade = useCallback(async () => {
    closeModal();
    dispatch(startUpgradeProcess());
  }, [dispatch, closeModal]);

  const seeAllPlans = useCallback(() => {
    if (!allowUpgrade) return;

    window.parent.postMessage(
      {
        type: AAASMessages.SEE_ALL_PLANS_CLICKED,
        data: seeAllPlansMessage,
      },
      '*',
    );
    return;
  }, [allowUpgrade, seeAllPlansMessage]);

  const tabs = useMemo<TabbedButtonProps['buttonOptions']>(
    () => [
      {
        value: SubscriptionTypeEnum.MONTHLY,
        label: translate('modals.freeTrialEnding.tabs.monthly'),
        disabled: isLoading,
      },
      {
        value: SubscriptionTypeEnum.YEARLY,
        label: translate('modals.freeTrialEnding.tabs.yearly'),
        disabled: isLoading,
      },
    ],
    [translate, isLoading],
  );

  return (
    <ModalWrapper isModalOpen={isOpen} closeModal={closeModal} contentClassName='sm:rounded-24 rounded-30 sm:w-full'>
      <div className='max-w-[45rem] sm:p-3 sm:pt-18px p-18px pt-6 overflow-hidden'>
        <Heading3 className='text-center text-wrap' weight={fontWeights.semibold}>
          {title}
        </Heading3>
        <Paragraph className='text-center' weight={fontWeights.medium}>
          {subtitle}
        </Paragraph>

        <TabbedButton
          className='my-3 h-12!'
          buttonOptions={tabs}
          onChange={setSubscriptionType}
          selectedValue={subscriptionType}
        />

        <div className='mb-3'>
          {isLoading || !recommendedPlan ? (
            <Skeleton
              className='block rounded-12'
              containerClassName='block w-full mb-1.5'
              inline
              count={1}
              height={90}
            />
          ) : (
            <PricingPlanTile
              selected={selectedPlanId === recommendedPlan.id}
              planFeature={translate('modals.freeTrialEnding.pricingPlanTile.creditsIncluded', {
                count: addNumberSeparator(recommendedPlan?.touchpoints, locale),
              })}
              title={recommendedPlan.name}
              onClick={() => setSelectedPlanId(recommendedPlan.id)}
              className='mb-1.5'
              price={recommendedPlan.price}
              recommended
            />
          )}
        </div>

        <div className='flex sm:flex-col gap-1.5 overflow-hidden mb-3'>
          {isLoading || !otherPlans.length ? (
            <Skeleton
              className='block rounded-12'
              containerClassName='block flex gap-1.5 w-135'
              count={2}
              height={82}
              inline
            />
          ) : (
            otherPlans.map((plan) => (
              <PricingPlanTile
                key={plan.id}
                selected={plan.id === selectedPlanId}
                planFeature={translate('modals.freeTrialEnding.pricingPlanTile.creditsIncluded', {
                  count: addNumberSeparator(plan?.touchpoints, locale),
                })}
                onClick={() => setSelectedPlanId(plan.id)}
                title={plan.name}
                price={plan.price}
              />
            ))
          )}
          {allowUpgrade && (
            <Button
              className='rounded-18'
              textClasses='text-wrap'
              text={translate('modals.freeTrialEnding.allPlansBtn')}
              color='secondary'
              onClick={seeAllPlans}
              disabled={isLoading}
            />
          )}
        </div>

        <div className='px-18px py-3 bg-gray-wild-sand rounded-12'>
          <Paragraph size={paragraphSizes.tiny}>
            <span className='font-semibold'>{translate('modals.freeTrialEnding.hint')}</span>&nbsp;
            {hint}
          </Paragraph>
        </div>

        <div className='flex gap-3 mt-3'>
          <Button
            className='flex-1'
            color='quinary'
            text={translate('modals.freeTrialEnding.continueOnLimitedFreePLan')}
            onClick={closeModal}
          />
          {allowUpgrade && (
            <Button
              className='flex-1'
              text={translate('modals.freeTrialEnding.submitBtn')}
              disabled={isLoading}
              onClick={handleUpgrade}
            />
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};
